import { AfterViewChecked, Component, Input, OnInit, QueryList, ViewChildren } from "@angular/core";
import { ArrayHelper, DateHelper, IdHelper } from "@osapp/helpers";
import { EPrefix, IContact } from "@osapp/model";
import { DestroyableComponentBase } from "@osapp/modules/utils/components/destroyable-component-base";
import { ContactsService } from "@osapp/services";
import { StoredSeance } from "apps/idl/src/anakin/models/StoredSeance";
import { ITransmissionRapport } from "apps/idl/src/modules/patients/model/ITransmissionRapport";
import { RapportService } from "apps/idl/src/modules/patients/services/rapport.service";
import { TraitementService } from "apps/idl/src/services/traitement.service";
import { Observable, of } from "rxjs";
import { debounceTime, delay, map, merge as mergeOperator, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { IPatient } from "../../../../../modules/patients/model/IPatient";
import { CarteSeanceComponent } from "../../../shared/components/seance/carte-seance.component";
import { DeviceService } from "../../../shared/services/device.service";
import { LoaderService } from "../../../shared/services/loader.service";
import { SeanceService } from "../../../shared/services/seance.service";
import { TransmissionService } from "../../../shared/services/transmission.service";
import { PatientsService } from "apps/idl/src/modules/patients/services/patients.service";
@Component({
	selector: "di-fil-de-soin",
	templateUrl: "./fil-de-soin.component.html",
	styleUrls: ["./fil-de-soin.component.scss"],
})
export class ListeFilDeSoinComponent extends DestroyableComponentBase implements OnInit, AfterViewChecked {
	@ViewChildren(CarteSeanceComponent) carteSeanceList: QueryList<CarteSeanceComponent>;
	
	@Input() public patient?: IPatient;

	public noMoreSeances: boolean = false;
	public infirmiersWS: IContact[] = [];
	public idsTraitement: string[] = [];
	public seances: StoredSeance[] = [];
	public upcomingSeances: StoredSeance[] = [];
	public showUpcoming: boolean = false;

	public groupedData: {
		date: string;
		seances: StoredSeance[];
		transmissions: ITransmissionRapport[];
		sortDate?: Date;
	}[] = [];

	public visibleGroupedSeances: {
		date: string;
		seances: StoredSeance[];
		transmissions: ITransmissionRapport[];
	}[] = [];

	public transmissions: ITransmissionRapport[] = [];

	public isMobileView: boolean = false;
	public targetSeanceStartDate: Date;
	public targetSeanceId: string = "";
	private seanceScrolledTo: string = "";

	constructor(
		private svcSeance: SeanceService,
		private svcRapport: RapportService,
		private svcDevice: DeviceService,
		private svcContact: ContactsService,
		private svcLoader: LoaderService,
		private svcTraitement: TraitementService,
		private svcTransmission: TransmissionService,
		private svcPatients: PatientsService
	) {
		super();
	}

	ngOnInit() {
		this.svcDevice.isMobile$
			.pipe(takeUntil(this.destroyed$))
			.subscribe((flag: boolean) => {
				this.isMobileView = flag;
			});

		this.svcSeance.refreshSeanceList$.pipe(
			takeUntil(this.destroyed$),
			// Permet de gérer le cas où plusieurs séances sont réalisées d'un coup, pour exécuter loadListSeances une seule fois
			debounceTime(200),
			tap(() => this.loadListSeances())
		).subscribe();

		this.svcTransmission.refreshTransmissionList$.pipe(
			takeUntil(this.destroyed$),
			tap(() => this.loadListTransmission())
		).subscribe();

		this.svcPatients.patientChanges$.pipe(
			takeUntil(this.destroyed$)
		).subscribe(() => {
			this.initValueComposant();
			this.loadListData();
		}); 

		this.initValueComposant();
		this.loadListData();
	}

	ngAfterViewChecked(): void {
		if (this.targetSeanceId && this.seanceScrolledTo !== this.targetSeanceId) {
			const targetComponent = this.carteSeanceList.toArray().find((carteSeance: CarteSeanceComponent) => carteSeance.seance._id === this.targetSeanceId);
			if (targetComponent && targetComponent.seanceElement) {
				const targetElement = targetComponent.seanceElement.nativeElement;
				targetElement.scrollIntoView({ behavior: 'auto', block: 'center' });
				this.seanceScrolledTo = this.targetSeanceId;
			}
		}
	}

	private initInfirmiers(): Observable<void> {
		return this.svcContact.getSiteContactsAnakin([], EPrefix.contact, true, true)
			.pipe(
				map((contacts: IContact[]) => contacts.filter((contact: IContact) => contact.userId)),
				tap((filteredContacts: IContact[]) => {
					// On stocke tous les infirmiers du workspace
					this.infirmiersWS = filteredContacts || [];
				}),
				takeUntil(this.destroyed$),
				map(() => undefined) // pour que l'Observable retourne `void`
			);
	}

	private loadListData() {
		this.initInfirmiers().pipe(
			tap(() => this.loadListSeances()),
			tap(() => this.loadListTransmission())
		).subscribe();
	}

	private loadListSeances() {
		this.svcSeance
			.selectSeancesByPatientView(this.patient._id)
			.pipe(
				tap(() => this.svcLoader.showLoader("Chargement des seances ...")),
				tap((seances: StoredSeance[]) => {
					if (!ArrayHelper.hasElements(seances)) {
						this.seances = [];
					}
					const today = new Date();
					let idsTraitement: Set<string> = new Set();
					seances.forEach((seance) => {
						if (ArrayHelper.hasElements(this.infirmiersWS)) {
							//On récupère l'infirmier en lien avec la séance pour éviter de le récupérer x fois
							seance.infirmier = this.infirmiersWS.find(x => x._id === seance.infirmierId);
						}
						//Récupération des ids de traitements des séances
						idsTraitement.add(seance.traitementId);
					});

					if (idsTraitement.size > 0) {
						this.idsTraitement = Array.from(idsTraitement).filter(id => id);
					}

					this.seances = seances.filter(
						(seance) => new Date(seance.startDate) <= today
					);
					this.upcomingSeances = seances.filter(
						(seance) => new Date(seance.startDate) > today
					);

					this.manageSeances();
				}),
				switchMap((_) => {
					if (!ArrayHelper.hasElements(this.idsTraitement)) return of(null);

					return this.svcTraitement.getTraitementByIds(this.idsTraitement).pipe(
						tap((traitements) => {
							this.seances.forEach(seance => {
								seance.ordonnance = traitements.find(trait => trait._id === seance.traitementId)
							});
							this.upcomingSeances.forEach(seance => {
								seance.ordonnance = traitements.find(trait => trait._id === seance.traitementId)
							});
						})
					);
				}),
				delay(200),
				tap(() => {
					this.svcLoader.hideLoader();
				}),
				takeUntil(this.destroyed$)
			)
			.subscribe();
	}
	
	private loadUpcomingSeances() {
		this.showUpcoming = false;
		this.seances = [...this.seances, ...this.upcomingSeances];
		this.groupDataByDate();
	}

	private initValueComposant() {
		this.seances = [];
		this.groupedData = [];
		this.visibleGroupedSeances = [];
		this.showUpcoming = false;
	}

	private manageSeances() {
		this.showUpcoming = ArrayHelper.hasElements(this.upcomingSeances);
		this.groupDataByDate();

		if (!ArrayHelper.hasElements(this.seances)) return;

		this.scrollToSeance();
	}
	private groupDataByDate(): void {
		const dataByDate: {
			[key: string]: {
				seances?: StoredSeance[];
				transmissions?: ITransmissionRapport[];
				sortDate?: Date;
			};
		} = {};
		const today = new Date();

		this.seances.sort(
			(a, b) =>
				new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
		);

		this.seances.forEach((seance) => {
			const dateKey = this.getFormattedDate(new Date(seance.startDate), today);
			if (!dataByDate[dateKey]) {
				dataByDate[dateKey] = { sortDate: new Date(seance.startDate) };
			}
			if (!dataByDate[dateKey].seances) {
				dataByDate[dateKey].seances = [];
			}
			dataByDate[dateKey].seances.push(seance);
		});

		this.transmissions.forEach((transmission) => {
			const dateKey = this.getFormattedDate(
				new Date(transmission.createdDate),
				today
			);
			if (!dataByDate[dateKey]) {
				dataByDate[dateKey] = { sortDate: new Date(transmission.createdDate) };
			}
			if (!dataByDate[dateKey].transmissions) {
				dataByDate[dateKey].transmissions = [];
			}
			dataByDate[dateKey].transmissions.push(transmission);
		});

		this.groupedData = Object.keys(dataByDate).map((date) => ({
			date: date,
			seances: dataByDate[date].seances || [],
			transmissions: dataByDate[date].transmissions || [],
			sortDate: dataByDate[date].sortDate,
		}));
		this.groupedData.sort(
			(a, b) => new Date(b.sortDate).getTime() - new Date(a.sortDate).getTime()
		);
	}

	public getFormattedDate(date: Date, today: Date): string {
		const dateOnly = new Date(
			date.getFullYear(),
			date.getMonth(),
			date.getDate()
		);
		const todayOnly = new Date(
			today.getFullYear(),
			today.getMonth(),
			today.getDate()
		);

		const isToday = DateHelper.compareTwoDates(dateOnly, todayOnly) === 0;

		return isToday
			? `Aujourd'hui, ${date.toLocaleDateString("fr-FR", {
				weekday: "long",
				year: "numeric",
				month: "long",
				day: "numeric",
			})}`
			: date.toLocaleDateString("fr-FR", {
				weekday: "long",
				year: "numeric",
				month: "long",
				day: "numeric",
			});
	}

	public scrollToSeance(): void {
		// Permet de recevoir la startDate de la séance qui vient d'être mis à jour / supprimée
		this.svcSeance.seanceUpdated$.pipe(
			mergeOperator(of(null)),
			take(1)
		).subscribe((seanceStartDate: Date | null) => {
			if (seanceStartDate) {
				this.targetSeanceStartDate = new Date(seanceStartDate);
				this.svcSeance.clearSeanceUpdated();
			}

			if(!this.targetSeanceStartDate || !this.seances?.length) return;

			const firstSeanceDate = new Date(this.seances[0].startDate);
			if (firstSeanceDate <= this.targetSeanceStartDate) {
				this.loadUpcomingSeances();
			}

			for (const seance of this.seances) {
				const startDate: Date = new Date(seance.startDate);
					if (startDate <= this.targetSeanceStartDate) {
						this.targetSeanceId = seance._id;
						break; 
					}
				}
			}
		);
	}

	public showUpcomingSeances(): void { }

	public getIdPatient(string: string): string {
		return `${EPrefix.patient}${IdHelper.getGuidFromId(string, EPrefix.patient).split(
			IdHelper.C_ID_SEPARATOR
		)[0]
			}`;
	}

	public getIdContact(string: string): string {
		return `${EPrefix.contact}${IdHelper.getGuidFromId(string, EPrefix.contact)}`;
	}

	private loadListTransmission(): void {
		this.initTransmissions()
			.pipe(
				tap(() => this.svcLoader.showLoader("Chargement des transmissions ...")),
				tap(() => {
					this.transmissions.forEach(trans => {
						trans.patient = this.patient;

						// Récupération du contactId de chaque transmission
						if (!trans.contactId) {
							trans.contactId = this.getIdContact(trans.authorPath);
						}
						trans.author = this.infirmiersWS.find(x => x._id === trans.contactId);

						// Récupération du contactId pour chaque commentaire
						if (ArrayHelper.hasElements(trans.commentaires)) {
							trans.commentaires.forEach(com => {
								com.auteur = this.infirmiersWS.find(x => x._id === com.auteurId);
							});
						}
					});
				}),
				tap(() => {
					this.groupDataByDate();
				}),
				tap(() => {
					this.svcLoader.hideLoader();
				}),
				takeUntil(this.destroyed$)
			)
			.subscribe();
	}

	private initTransmissions(): Observable<ITransmissionRapport[]> {
		return this.svcRapport.getTransmissions(this.patient._id, undefined, false).pipe(
			tap((transmissions: ITransmissionRapport[]) => {
				this.transmissions = transmissions.map(transmission => {
					const dateTrans: Date = transmission.dateLastModification ?? transmission.createdDate;
					return {
						...transmission,
						dateLastModification: new Date(dateTrans)
					};
				});

				this.transmissions.sort((a, b) => {
					return (b.dateLastModification as Date).getTime() - (a.dateLastModification as Date).getTime();
				});
			})
		);
	}

}
